import { Typography, Card, CardHeader, CardContent } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { TestCase } from "../../services/evals";
import { formatDateTime } from "../utils";

interface TestCaseRowViewModeProps {
  testCase: TestCase;
}

export const TestCaseRowViewMode: React.FC<TestCaseRowViewModeProps> = ({ testCase: tc }) => {
  return (
    <>
      {tc.description && (
        <div className="al-text-sm al-text-gray-600 al-mb-4">{tc.description}</div>
      )}

      <div className="al-text-sm al-text-gray-600 al-mb-4">
        <span className="al-font-semibold">Created:</span> {formatDateTime(tc.created_at)}
      </div>

      {/* User View / Page Context */}
      {tc.user_view && Object.keys(tc.user_view).length > 0 && (
        <div className="al-mb-4">
          <Typography
            size="xs"
            weight="regular"
            className="al-text-violet-700 al-uppercase al-mb-2 al-block"
          >
            Page Context
          </Typography>
          <div className="al-bg-violet-50 al-border al-border-violet-200 al-p-3 al-rounded-lg al-text-sm">
            <Stack direction="row" gap="md" className="al-flex-wrap">
              {Object.entries(tc.user_view).map(([key, value]) => (
                <div key={key}>
                  <span className="al-font-semibold al-text-violet-700">{key}:</span>{" "}
                  <span className="al-text-gray-900">
                    {typeof value === "object" ? JSON.stringify(value) : String(value)}
                  </span>
                </div>
              ))}
            </Stack>
          </div>
        </div>
      )}

      {/* Custom Agent - View Mode */}
      {tc.custom_agents && tc.custom_agents.length > 0 && (
        <div className="al-mb-4">
          <Typography
            variant="body"
            weight="medium"
            className="al-text-emerald-700 al-uppercase al-mb-2 al-block"
          >
            Custom Agents
          </Typography>
          <Stack direction="column" gap="md">
            {tc.custom_agents.map((agent, index) => (
              <div
                key={index}
                className="al-bg-emerald-50 al-border al-border-emerald-200 al-p-4 al-rounded-lg"
              >
                <Typography variant="body" weight="medium" className="al-text-emerald-700 al-mb-3">
                  Agent {index + 1}
                </Typography>
                <div className="al-mb-2">
                  <Typography variant="body" weight="medium" className="al-text-gray-700">
                    Name:
                  </Typography>
                  <Typography className="al-text-gray-900">{agent.name}</Typography>
                </div>
                <div className="al-mb-2">
                  <Typography variant="body" weight="medium" className="al-text-gray-700">
                    Description:
                  </Typography>
                  <Typography className="al-text-gray-900">{agent.description}</Typography>
                </div>
                <div>
                  <Typography variant="body" weight="medium" className="al-text-gray-700">
                    Prompt:
                  </Typography>
                  <div className="al-bg-white al-border al-border-emerald-200 al-p-3 al-rounded al-text-sm al-whitespace-pre-wrap al-font-mono">
                    {agent.prompt}
                  </div>
                </div>
              </div>
            ))}
          </Stack>
        </div>
      )}

      {/* Turns - View Mode */}
      <div className="al-space-y-4">
        {tc.turns.map((turn, index) => (
          <div key={index}>
            <Card className="al-bg-white">
              <CardHeader className="al-pb-3">
                <Typography variant="body" weight="medium">
                  Turn {index + 1}
                </Typography>
              </CardHeader>
              <CardContent>
                <div className="al-space-y-3">
                  <div>
                    <Typography
                      variant="body"
                      weight="medium"
                      className="al-text-orange-700 al-uppercase al-mb-1 al-block"
                    >
                      User Message
                    </Typography>
                    <div className="al-bg-orange-50 al-border al-border-orange-200 al-p-3 al-rounded-lg al-text-sm al-whitespace-pre-wrap">
                      {turn.user_message}
                    </div>
                  </div>

                  <div>
                    <Typography
                      variant="body"
                      weight="medium"
                      className="al-text-teal-700 al-uppercase al-mb-1 al-block"
                    >
                      Expected AI Response
                    </Typography>
                    <div className="al-bg-teal-50 al-border al-border-teal-200 al-p-3 al-rounded-lg al-text-sm al-whitespace-pre-wrap">
                      {turn.expected_ai_response}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    </>
  );
};
